import React from 'react';

import find from 'lodash/find';
import {useSelector} from 'react-redux';
import {Link} from 'react-router';

import {
  GenericAttachedItemsSidebarItem,
  GenericSidebarItemStructureActionButton,
} from 'app/components/general/GenericAttachedItemsSidebar/GenericAttachedItemsSidebarItem';
import {RTKGenericAttachedItemsSidebar} from 'app/components/general/GenericAttachedItemsSidebar/RTKGenericAttachedItemsSidebar';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {CurrentUserSelector} from 'app/current-user/api';
import {
  fetchProcessLatestVersion,
  updateProcessVersion,
} from 'app/process/actions';
import {
  convertLocationParamsToFetchParams,
  useApproveProcessVersion,
  useProcesses,
} from 'app/process/api';
import {sweetAPI} from 'app/store/sweet-api';
import {
  type ModelChangeMessageType,
  runWhenAllRelatedMatch,
} from 'app/subscriptions';
import {PROCESS_CREATE_PAGE_URL} from 'app/urls';
import {reportUnexpectedState} from 'app/utils/errors';
import {getProcessName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';
import {PROCEDURE_RTK_TAGS, PROCESS_RTK_TAGS} from 'app/utils/rtk-tags';

import type {
  CustomSidebarItemProps,
  Props as SidebarItemProps,
} from 'app/components/general/GenericAttachedItemsSidebar/GenericAttachedItemsSidebarItem';
import type {
  ProcedureHashidSerializedType,
  ProcessSerializedType,
} from 'sweet-types';

type Props = {
  procedure: ProcedureHashidSerializedType;
  legacyRefetchData: () => Promise<unknown>;
};

export const ProcedureProcessesSidebar = ({
  procedure,
  legacyRefetchData,
}: Props) => {
  const user = useSelector((state) => CurrentUserSelector(state).data!);

  const actions = useAsyncActions({
    fetchProcessLatestVersion,
    updateProcessVersion,
    invalidateTags: sweetAPI.util.invalidateTags,
  });

  const [approveProcessVersion] = useApproveProcessVersion();

  const getItemComponentProps = (
    process: ProcessSerializedType,
    attached: boolean
  ): CustomSidebarItemProps<ProcessSerializedType> => {
    const hasDrafts = process.recent_draft.id !== process.current_version.id;
    return {
      disableAndShowTooltipText: hasDrafts
        ? gt('This process has unapproved versions, click to review')
        : !procedure.can_edit
        ? gt('Only procedure managers can add to or remove from process')
        : !process.can_edit
        ? gt('Only this process managers can add or remove procedures')
        : undefined,
      actionTooltip: attached
        ? gt('Remove procedure from this process')
        : gt('Add procedure to this process'),
    };
  };

  function getItemComponent(props: SidebarItemProps<ProcessSerializedType>) {
    const hasDrafts =
      props.item.recent_draft.id !== props.item.current_version.id;
    return (
      <GenericAttachedItemsSidebarItem
        {...props}
        getActionButtonComponent={(actionButtonProps) =>
          hasDrafts ? (
            <Link
              className="btn btn btn-link p-2"
              to={`${props.item.html_url}edit/`}
            >
              <i className="fa fa-fw fa-pen" />
            </Link>
          ) : (
            <GenericSidebarItemStructureActionButton {...actionButtonProps} />
          )
        }
      />
    );
  }

  const onRemove = (process: ProcessSerializedType) =>
    actions.fetchProcessLatestVersion(process.id).then((version) => {
      const membership = find(version.content, {
        url: procedure.url,
      });

      if (membership != null) {
        return actions
          .updateProcessVersion(version.url, {
            content: [
              {
                id: procedure.hashid,
                procedure_id: procedure.id,
                title: procedure.name,
                type: 'processprocedure',
                n: version.content.length + 1,
                delete: true,
              },
            ],
          })
          .then((processVersion) =>
            approveProcessVersion({
              versionURL: processVersion.url,
              documentId: process.id,
              customNotificationText:
                'A new version without this procedure was' +
                ' created and approved',
              options: {notify: false, signoff: false},
            })
          )
          .then(() => legacyRefetchData());
      }

      return Promise.resolve(
        reportUnexpectedState('USEC12', {
          procedureURL: procedure.url,
          process,
        })
      );
    });

  const onAttach = (process: ProcessSerializedType) =>
    actions.fetchProcessLatestVersion(process.id).then((version) =>
      actions
        .updateProcessVersion(version.url, {
          content: [
            {
              id: procedure.hashid,
              procedure_id: procedure.id,
              title: procedure.name,
              type: 'processprocedure',
              n: version.content.length + 1,
            },
          ],
        })
        .then((processVersion) =>
          approveProcessVersion({
            versionURL: processVersion.url,
            documentId: process.id,
            customNotificationText:
              'A new version with this procedure was created' + ' and approved',
            options: {notify: false, signoff: false},
          })
        )
        .then(() => legacyRefetchData())
    );

  return (
    <RTKGenericAttachedItemsSidebar
      title={'Processes'}
      closeUrl={procedure.html_url}
      attachedItems={procedure.processes}
      itemNameProcessor={getProcessName}
      itemComponentProps={getItemComponentProps}
      ItemComponent={getItemComponent}
      onAttach={onAttach}
      onRemove={onRemove}
      useDataHook={useProcesses}
      showFilterField={user.is_manager && procedure.can_edit}
      searchPlaceholder={gt('Search processes...')}
      canCreate={
        user.team_memberships.filter(
          (membership) => membership.access != 'reader'
        ).length > 0 ||
        user.is_manager ||
        user.is_super_teammate
      }
      createLabel={gt('Create Process')}
      createURL={PROCESS_CREATE_PAGE_URL}
      handleModelChange={(message: ModelChangeMessageType) => {
        runWhenAllRelatedMatch(
          message,
          'processversion',
          {
            content_type: 'process',
          },
          () => {
            if (legacyRefetchData != null) {
              legacyRefetchData();
            }
            actions.invalidateTags([
              // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
              {type: PROCESS_RTK_TAGS.TAGS, id: 'list'},
              // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
              {type: PROCEDURE_RTK_TAGS.TAGS, id: procedure.url},
            ]);
          }
        );
      }}
      emptyResultsComponents={
        <NoResultsMessage
          onSidebar={true}
          icon="fa-copy"
          head={gt('No processes')}
        >
          {user.is_manager && procedure.can_edit
            ? gt(
                'You can add this procedure to existing processes, remove it from a process or create a new process using the button below'
              )
            : null}
        </NoResultsMessage>
      }
      paramsConverter={convertLocationParamsToFetchParams}
    />
  );
};
